"use client";

import { useEffect, useRef, useState } from "react";
import { useStore } from "@/lib/store";
import type { StatusId, PriorityId } from "@/lib/types";
import { Modal } from "@/components/ui/Modal";
import { StatusIcon } from "@/components/ui/StatusIcon";
import { PriorityIcon } from "@/components/ui/PriorityIcon";
import { Avatar } from "@/components/ui/Avatar";
import { ReportChannelPicker } from "./ReportChannel";
import { parseTaskReportOrigin, type TaskReportChannel } from "@/lib/taskReportOrigin";
import { STATUSES, PRIORITIES } from "@/lib/constants";
import {
  StatusPicker,
  PriorityPicker,
  AssigneePicker,
  LabelPicker,
  ProjectPicker,
  TeamPicker,
} from "./Pickers";

export function CreateIssueModal({
  defaultProjectId = null,
  defaultTeamId = null,
}: {
  defaultProjectId?: string | null;
  defaultTeamId?: string | null;
}) {
  const open = useStore((s) => s.createOpen);
  const setOpen = useStore((s) => s.setCreateOpen);
  const teams = useStore((s) => s.teams);
  const users = useStore((s) => s.users);
  const labels = useStore((s) => s.labels);
  const projects = useStore((s) => s.projects);
  const activeTeamId = useStore((s) => s.activeTeamId);
  const createIssue = useStore((s) => s.createIssue);
  const selectIssue = useStore((s) => s.selectIssue);
  const currentUserId = useStore((s) => s.currentUserId);
  const currentHumanId = users.find((user) => user.id === currentUserId && !user.isBot)?.id ?? null;

  const [teamId, setTeamId] = useState<string>(activeTeamId || teams[0]?.id || "");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<StatusId>("todo");
  const [priority, setPriority] = useState<PriorityId>(0);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [executorAgentId, setExecutorAgentId] = useState<string | null>(null);
  const [requesterId, setRequesterId] = useState<string | null>(null);
  const [reportChannel, setReportChannel] = useState<TaskReportChannel>("pauta");
  const [reportContext, setReportContext] = useState("");
  const [reportUrl, setReportUrl] = useState("");
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [projectId, setProjectId] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      const defaultProject = projects.find(
        (candidate) => candidate.id === defaultProjectId
      );
      const compatibleTeamId =
        defaultTeamId && defaultProject?.teamIds.includes(defaultTeamId)
          ? defaultTeamId
          : activeTeamId && defaultProject?.teamIds.includes(activeTeamId)
            ? activeTeamId
            : defaultProject?.teamIds[0] ??
              activeTeamId ??
              teams[0]?.id ??
              "";
      setTeamId(compatibleTeamId);
      setTitle("");
      setDescription("");
      setStatus("todo");
      setPriority(0);
      setOwnerId(null);
      setExecutorAgentId(null);
      setRequesterId(currentHumanId);
      setReportChannel("pauta");
      setReportContext("");
      setReportUrl("");
      setLabelIds([]);
      setProjectId(defaultProjectId);
      setTimeout(() => titleRef.current?.focus(), 40);
    }
  }, [
    open,
    activeTeamId,
    teams,
    projects,
    defaultProjectId,
    defaultTeamId,
    currentHumanId,
  ]);

  const team = teams.find((t) => t.id === teamId);
  const owner = users.find((u) => u.id === ownerId);
  const executorAgent = users.find((u) => u.id === executorAgentId);
  const requester = users.find((u) => u.id === requesterId && !u.isBot);
  const project = projects.find((p) => p.id === projectId);
  const selectedLabels = labels.filter((l) => labelIds.includes(l.id));
  const statusMeta = STATUSES.find((s) => s.id === status)!;
  const priorityMeta = PRIORITIES.find((p) => p.id === priority)!;

  const projectMatchesTeam =
    !project || project.teamIds.length === 0 || project.teamIds.includes(teamId);
  const reportOrigin = parseTaskReportOrigin({
    report_channel: reportChannel,
    report_context: reportContext || null,
    report_url: reportUrl || null,
  });
  const canSubmit =
    title.trim().length > 0 && Boolean(teamId) && projectMatchesTeam && reportOrigin.ok;

  const submit = (openAfter: boolean) => {
    if (!canSubmit || !reportOrigin.ok) return;
    const issue = createIssue({
      title: title.trim(),
      description: description.trim(),
      teamId,
      status,
      priority,
      ownerId,
      executorAgentId,
      requesterId,
      reportChannel: reportOrigin.value.report_channel,
      reportContext: reportOrigin.value.report_context,
      reportUrl: reportOrigin.value.report_url,
      labelIds,
      projectId,
    });
    setOpen(false);
    if (openAfter) selectIssue(issue.id);
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)} width={640} ariaLabel="New task">
      <div
        className="min-h-0 overflow-y-auto scrollbar-thin"
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            submit(true);
          }
        }}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--border)]">
          <TeamPicker
            value={teamId}
            onChange={(nextTeamId) => {
              setTeamId(nextTeamId);
              setLabelIds([]);
              if (
                project &&
                project.teamIds.length > 0 &&
                !project.teamIds.includes(nextTeamId)
              ) {
                setProjectId(null);
              }
            }}
            trigger={
              <button className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-[var(--bg-hover)] text-[12px] text-[var(--fg-secondary)]">
                <span
                  className="w-4 h-4 rounded-[4px] flex items-center justify-center text-[10px]"
                  style={{ background: `${team?.color}22`, color: team?.color }}
                >
                  {team?.icon}
                </span>
                <span className="font-medium">{team?.name}</span>
              </button>
            }
          />
          <span className="text-[var(--fg-muted)] text-[12px]">›</span>
          <span className="text-[12px] text-[var(--fg-secondary)]">New task</span>
        </div>

        <div className="px-5 py-4 space-y-2">
          <input
            ref={titleRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Task title"
            className="w-full bg-transparent outline-none text-[17px] font-semibold placeholder:text-[var(--fg-muted)]"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add description…"
            className="w-full bg-transparent outline-none text-[13px] text-[var(--fg-secondary)] placeholder:text-[var(--fg-muted)] resize-none min-h-[80px]"
          />
        </div>

        <div className="px-4 pb-3 flex flex-wrap items-center gap-1.5">
          <StatusPicker
            value={status}
            onChange={setStatus}
            trigger={
              <Pill>
                <StatusIcon status={status} size={12} />
                <span>{statusMeta.name}</span>
              </Pill>
            }
          />
          <PriorityPicker
            value={priority}
            onChange={setPriority}
            trigger={
              <Pill>
                <PriorityIcon priority={priority} size={12} />
                <span>{priorityMeta.name}</span>
              </Pill>
            }
          />
          <AssigneePicker
            kind="human"
            value={ownerId}
            onChange={setOwnerId}
            unassignedLabel="No owner"
            trigger={
              <Pill>
                <Avatar user={owner} size={16} />
                <span>{owner?.name || "Owner"}</span>
              </Pill>
            }
          />
          <AssigneePicker
            kind="agent"
            value={executorAgentId}
            onChange={setExecutorAgentId}
            unassignedLabel="No agent"
            trigger={
              <Pill>
                <Avatar user={executorAgent} size={16} />
                <span>{executorAgent?.name || "Agent"}</span>
              </Pill>
            }
          />
          <AssigneePicker
            kind="human"
            value={requesterId}
            onChange={setRequesterId}
            unassignedLabel="No reporter"
            trigger={
              <Pill>
                <Avatar user={requester} size={16} />
                <span>Reporter{requester ? `: ${requester.name}` : ""}</span>
              </Pill>
            }
          />
          {teamId && (
            <LabelPicker
              teamId={teamId}
              value={labelIds}
              onChange={setLabelIds}
              trigger={
                <Pill>
                  {selectedLabels.length === 0 ? (
                    <span>Labels</span>
                  ) : (
                    <>
                      {selectedLabels.slice(0, 2).map((l) => (
                        <span
                          key={l.id}
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ background: l.color }}
                        />
                      ))}
                      <span>
                        {selectedLabels.length} label{selectedLabels.length > 1 ? "s" : ""}
                      </span>
                    </>
                  )}
                </Pill>
              }
            />
          )}
          <ProjectPicker
            value={projectId}
            teamId={teamId}
            onChange={setProjectId}
            trigger={
              <Pill>
                {project ? (
                  <>
                    <span>{project.icon}</span>
                    <span className="truncate max-w-[120px]">{project.name}</span>
                  </>
                ) : (
                  <span>Project</span>
                )}
              </Pill>
            }
          />
        </div>

        <div className="space-y-2 border-t border-[var(--border)] px-5 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[12px] text-[var(--fg-tertiary)]">Canal do reporte</span>
            <ReportChannelPicker
              value={reportChannel}
              onChange={(channel) => {
                setReportChannel(channel);
                setReportContext("");
                setReportUrl("");
              }}
            />
          </div>
          {reportChannel !== "pauta" ? (
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="space-y-1 text-[11.5px] text-[var(--fg-tertiary)]">
                <span>Context (optional)</span>
                <input
                  value={reportContext}
                  onChange={(e) => setReportContext(e.target.value)}
                  maxLength={180}
                  aria-invalid={!reportOrigin.ok && reportOrigin.error.startsWith("report_context")}
                  aria-describedby={!reportOrigin.ok ? "report-origin-error" : undefined}
                  placeholder={reportChannel === "slack" ? "#product" : "Session or source"}
                  className="block w-full rounded-md border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-[12px] text-[var(--fg-secondary)] outline-none focus:border-[var(--accent)]"
                />
              </label>
              <label className="space-y-1 text-[11.5px] text-[var(--fg-tertiary)]">
                <span>Source link (optional)</span>
                <input
                  type="url"
                  value={reportUrl}
                  onChange={(e) => setReportUrl(e.target.value)}
                  maxLength={2048}
                  placeholder="https://…"
                  aria-invalid={!reportOrigin.ok && reportOrigin.error.startsWith("report_url")}
                  aria-describedby={!reportOrigin.ok ? "report-origin-error" : undefined}
                  className="block w-full rounded-md border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-[12px] text-[var(--fg-secondary)] outline-none focus:border-[var(--accent)]"
                />
              </label>
              {!reportOrigin.ok ? (
                <p id="report-origin-error" role="alert" className="text-[11.5px] text-[var(--red)] sm:col-span-2">
                  {reportOrigin.error.replace("report_url", "Source link").replace("report_context", "Context").replace("report_channel", "Report channel")}
                </p>
              ) : null}
            </div>
          ) : null}
          <p className="text-[11px] text-[var(--fg-muted)]">The original report channel is kept when the task is updated.</p>
        </div>

        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-[var(--border)] bg-[var(--bg-secondary)]">
          <div className="text-[11px] text-[var(--fg-muted)]">
            <kbd className="kbd">⌘</kbd> <kbd className="kbd">Enter</kbd> to create
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOpen(false)}
              className="px-3 py-1.5 rounded-md text-[12.5px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)]"
            >
              Cancel
            </button>
            <button
              onClick={() => submit(false)}
              disabled={!canSubmit}
              className="px-3 py-1.5 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-40 text-white text-[12.5px] font-medium transition-colors"
            >
              Create task
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <button className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-[var(--border)] hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)] text-[12px] text-[var(--fg-secondary)] transition-colors">
      {children}
    </button>
  );
}
